<style scoped>
.no-items-row {
  color: rgb(102, 102, 102);
  font-size: 14px;
  padding: 10px 0px;
}
@media (min-width: 1333px) {
  .raised.p-3.rounded.mt-sm-3 {
    height: 98% !important;
  }
}

</style>

<template>
  <title-card title="Actions">

    <b-list-group class="mt-3">
      <b-list-group-item
        v-for="(docs, key) in filteredDocuments"
        v-bind:key="key"
        class="d-flex justify-content-between align-items-center"
      >
        {{ titleMap[key] }}
        <span
          v-b-tooltip.hover.left
          :title="getTooltip(key)"
          :class="{ 'pointer-cursor text-anchor': submissionAllowed(key) }"
        >
          <font-awesome-icon
            icon="file-signature"
            @click="show(key)"
          />
        </span>
      </b-list-group-item>
    </b-list-group>

    <draw-wizard-modal
      v-if="isDrawAllowed"
      ref="drawModal"
      :loan="loan"
      @draw-sent="(response) => $emit('draw-sent', response)"
      @new-total-balance="loan.draws_available_balance = $event"
    />
    <extension-wizard-modal
      v-if="isExtensionAllowed"
      ref="extensionWizardModal"
      :loan="loan"
      @extension-sent="(response) => $emit('extension-sent', response)"
    />
    <payoff-wizard-modal
      v-if="isPayoffAllowed"
      ref="payoffWizardModal"
      :loan="loan"
      @payoff-sent="(response) => $emit('payoff-sent', response)"
    />
    <ach-payment-wizard-modal
      ref="achPaymentModal"
      :loan="loan"
      @ach-payment-sent="(response) => $emit('ach-payment-sent', response)"
    />
    <change-order-wizard-modal
      ref="changeOrderModal"
      :loan="loan"
      @on-save="(response) => $emit('change-order-sent', response)"
    />
    <insurance-wizzard-modal
      ref="insuranceModal"
      :loan="loan"
      @on-save="(response) => $emit('insurance-sent', response)"
    />
  </title-card>
</template>

<script>
import TitleCard from '@/components/utils/TitleCard';
import CollapsableContainer from '@/components/utils/CollapsableContainer';
import FilterTable from '@/components/utils/FilterTable';

import DrawManagerMixin from '@/components/my_deals/actions/draw/DrawManagerMixin';
import DrawWizardModal from '@/components/my_deals/actions/draw/DrawWizardModal';

import ExtensionManagerMixin from '@/components/my_deals/actions/extension/ExtensionManagerMixin';
import ExtensionWizardModal from '@/components/my_deals/actions/extension/ExtensionWizardModal';

import PayoffManagerMixin from '@/components/my_deals/actions/payoff/PayoffManagerMixin';
import PayoffWizardModal from '@/components/my_deals/actions/payoff/PayoffWizardModal';

import AchPaymentManagerMixin from '@/components/my_deals/actions/ach_payment/resources/AchPaymentManagerMixin';
import AchPaymentWizardModal from '@/components/my_deals/actions/ach_payment/AchPaymentWizardModal';

import InsuranceManagerMixin from '@/components/my_deals/actions/insurance/resources/InsuranceManagerMixin';
import InsuranceWizzardModal from '@/components/my_deals/actions/insurance/InsuranceWizzardModal';

import ChangeOrderMixin from '@/components/my_deals/actions/change_order/ChangeOrderMixin';
import ChangeOrderWizardModal from '@/components/my_deals/actions/change_order/ChangeOrderWizardModal';

import { LOANS_ACH_READ, LOANS_DRAW_READ, LOANS_PAYOFF_READ } from '@/libs/permissions_list';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faFileSignature } from '@fortawesome/free-solid-svg-icons';

library.add([faFileSignature]);

export default {
  components: {
    TitleCard,
    CollapsableContainer,
    FilterTable,
    DrawWizardModal,
    ExtensionWizardModal,
    PayoffWizardModal,
    AchPaymentWizardModal,
    InsuranceWizzardModal,
    ChangeOrderWizardModal,
  },
  props: {
    loan: {
      type: Object,
      required: true,
    },
  },
  mixins: [
    DrawManagerMixin,
    ExtensionManagerMixin,
    PayoffManagerMixin,
    AchPaymentManagerMixin,
    InsuranceManagerMixin,
    ChangeOrderMixin,
  ],
  data() {
    return {
      documents: [],
      fields: [
        { key: 'name', sortable: true },
        { key: 'submitted', sortable: true },
      ],
      titleMap: {
        draws: 'Draw Requests',
        change_order: 'Change Order',
        extensions: 'Extension Requests',
        payoff: 'Payoff Requests',
        achpayment: 'One time ACH Interest Payment Request',
        insurance: 'Insurance Documents',
      },
      templates: [
        { key: 'name' },
        { key: 'submitted' },
      ],
    };
  },
  computed: {
    filteredDocuments() {
      if (!this.$permissions.checkPermissions([LOANS_DRAW_READ])) {
        delete this.documents.draws;
      }
      if (!this.$permissions.checkPermissions([LOANS_ACH_READ])) {
        delete this.documents.achpayment;
      }
      if (!this.$permissions.checkPermissions([LOANS_PAYOFF_READ])) {
        delete this.documents.payoff;
      }
      return this.documents;
    },
  },
  methods: {
    show(key) {
      switch (key) {
        case 'draws':
          if (this.isDrawAllowed) {
            this.$refs.drawModal.show();
          }
          break;
        case 'extensions':
          if (this.isExtensionAllowed) {
            this.$refs.extensionWizardModal.show();
          }
          break;
        case 'payoff':
          if (this.isPayoffAllowed) {
            this.$refs.payoffWizardModal.show();
          }
          break;
        case 'achpayment':
          if (this.isAchPaymentAllowed) {
            this.$refs.achPaymentModal.show();
          }
          break;
        case 'insurance':
          if (this.isInsuranceAllowed) {
            this.$refs.insuranceModal.show();
          }
          break;
        case 'change_order':
          if (this.isChangeOrderAllowed) {
            this.$refs.changeOrderModal.show();
          }
          break;
        default:
          break;
      }
    },
    submissionAllowed(key) {
      switch (key) {
        case 'draws':
          return this.isDrawAllowed;
        case 'extensions':
          return this.isExtensionAllowed;
        case 'payoff':
          return this.isPayoffAllowed;
        case 'achpayment':
          return this.isAchPaymentAllowed;
        case 'insurance':
          return this.isInsuranceAllowed;
        case 'change_order':
          return this.isChangeOrderAllowed;
        default:
          return false;
      }
    },
    getTooltip(key) {
      const title = `Submit ${this.titleMap[key]}`;
      switch (key) {
        case 'draws':
          return this.isDrawAllowed ? title : null;
        case 'extensions':
          return this.isExtensionAllowed ? title : null;
        case 'payoff':
          return this.isPayoffAllowed ? title : null;
        case 'achpayment':
          return this.isAchPaymentAllowed ? title : null;
        case 'insurance':
          return this.isInsuranceAllowed ? title : null;
        case 'change_order':
          return this.isChangeOrderAllowed ? title : null;
        default:
          return null;
      }
    },
  },
  mounted() {
    this.documents = this.loan.documents.asset_management;
  },
};
</script>



// WEBPACK FOOTER //
// src/components/my_deals/deals/documents/Actions.vue